import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FaGavel, FaUsers, FaHandHoldingHeart, FaFileAlt, FaExclamationTriangle, FaClipboardCheck, FaCalendarAlt, FaCheckCircle, FaInfoCircle } from "react-icons/fa";
import lawBanner from "../assets/images/law-banner.webp";

function ModernSlavery() {
  return (
    <div className="page-container">
      <motion.section
        className="policy-hero modern-hero"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(26,54,93,0.75) 0%, rgba(43,76,126,0.75) 100%), url(${lawBanner})`,
          /* first value applies to gradient layer, second to the image layer */
          backgroundSize: "cover, 70%",
          backgroundPosition: "center, center 35%",
          backgroundRepeat: "no-repeat, no-repeat",
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        <div className="policy-hero-content">
          <motion.h1
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6 }}
          >
            <span className="hero-lead">Modern Slavery Statement</span>
          </motion.h1>
          <motion.p
            className="policy-intro"
            initial={{ y: -10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            Neurovasc is committed to preventing modern slavery, human
            trafficking and forced child labour in our business and supply
            chains. This statement sets out the steps we take to identify and
            reduce risks across our operations.
          </motion.p>
        </div>
      </motion.section>

      <section className="policy-content modern-policy">
        <div className="policy-intro-cards">
          <div className="intro-card policy-block">
            <h3>Statement Overview</h3>
            <p>
              Neurovasc Ltd is opposed to all forms of exploitation, including
              slavery, human trafficking, and the use of child or forced labour.
              We are committed to making meaningful improvements to sustainable
              workplace conditions and business practices, including, but not
              limited to, the prevention of forced or trafficked labour. We do
              not employ children before they have reached the legal age to have
              completed their compulsory education, as defined by the relevant
              authorities.
            </p>
            <p>
              This statement is made in response to section 54 (1) part 6 of the
              Modern Slavery Act 2015 and affirms our commitment to preventing
              modern slavery and human trafficking across our operations and
              supply chains.
            </p>
          </div>
        </div>

        <div className="policy-block">
          <h2><span className="section-icon"><FaGavel /></span>Definitions</h2>
          <p>
            Modern slavery is an umbrella term that covers human trafficking,
            slavery, servitude, and forced labour. Child labour, as defined by
            the International Labour Organisation (ILO) Convention, is “Work
            by children under the age of 12; Work by children under the age of
            15 that prevents school attendance; and work by children under the
            age of 18 that is hazardous to the physical or mental health of
            the child”.
          </p>
        </div>

        <div className="policy-highlight-cards">
          <div className="highlight-card">
            <div className="highlight-icon" aria-hidden="true"><FaUsers /></div>
            <div className="highlight-body">
              <h3>Equal Opportunities</h3>
              <p>
                Neurovasc Ltd is an equal opportunities employer, committed to
                ensuring an inclusive, non-discriminatory and ethical working
                environment where all staff are respected and supported.
              </p>
            </div>
          </div>

          <div className="highlight-card">
            <div className="highlight-icon" aria-hidden="true"><FaGavel /></div>
            <div className="highlight-body">
              <h3>Ethical Supply & Business Relationships</h3>
              <p>
                We will not enter into business with organisations—UK or
                overseas—that knowingly support or engage in slavery,
                servitude, forced or compulsory labour. We act swiftly to
                address and terminate any relationship that breaches these
                principles.
              </p>
            </div>
          </div>
        </div>

        <div className="policy-block supply-chain-block">
          <h2><span className="section-icon"><FaUsers /></span>Supply Chain</h2>
          <p>
            We procure goods and services from a restricted range of UK and
            overseas suppliers. We do not tolerate or condone the use of
            modern slavery, child labour, forced labour, or human trafficking
            within our organisation or supply chain, with a zero-tolerance
            approach.
          </p>

          <p>
            Our suppliers and subcontractors are an important part of our
            success and culture; we expect each partner to conduct their
            business activities with the same commitment to ethical business
            practices as ourselves. Prior to any relationship agreement, we
            carry out extensive due diligence whereby compliance with our
            expectations must be demonstrated and committed to. Details of all
            subcontractors and suppliers can be provided, as well as due
            diligence findings and vetting forms. We (or our supply chain):
          </p>

          <ul>
            <li>Will not use forced or compulsory labour</li>
            <li>Will ensure that terms of employment are voluntary</li>
            <li>Will not hold passports of migrant workers</li>
            <li>Will comply with the minimum age requirements prescribed by applicable laws</li>
            <li>Will compensate workers with wages and benefits that meet or exceed the legally required minimum and will comply with overtime pay agreements</li>
            <li>Will abide by applicable law concerning the maximum hours of labour – such as the provisions of the Working time Directive or other applicable laws</li>
            <li>Will keep records of and be entirely transparent in complying with the above.</li>
          </ul>

          <p>
            Any supply chain partner who is found to engage in modern slavery
            or human trafficking will have their supply agreements terminated
            immediately. If any supply chain partner is found to be in
            violation of our policy statements or agreements, we will take
            prompt remedial measures to address the violation, including but
            not limited to termination of the business relationship and
            involvement of legal authority(s).
          </p>
        </div>

        <div className="policy-block">
          <h2><span className="section-icon"><FaClipboardCheck /></span>Policies Relating to the Modern Slavery Act 2015</h2>
          <p>The following policies are available to all staff:</p>
          <ul>
            <li>Code of conduct</li>
            <li>Equality, Diversity, and Inclusion Policy</li>
            <li>Grievance Policy</li>
            <li>Safeguarding Children and Vulnerable Adults Policy</li>
            <li>Whistleblowing Policy</li>
            <li>Bullying and Harassment Policy</li>
            <li>Recruitment Policy and Toolkit</li>
            <li>Procurement and Ethical Sourcing Policies</li>
            <li>Corporate Social Responsibility and Social Value Policy</li>
          </ul>
        </div>

        <div className="policy-block">
          <h2><span className="section-icon"><FaHandHoldingHeart /></span>Embedding the Principles</h2>
          <p>We will continue to embed the principles through:</p>
          <ul>
            <li>
              Ensuring staff are aware of the Modern Slavery Act 2015 and
              providing information regarding the appropriate action to take if
              they suspect a case of slavery, human trafficking, and forced
              child labour.
            </li>
            <li>
              Ensuring staff involved with procurement are aware of, and
              follow gov.uk Procurement Policy Note 05/19: Tackling Modern
              Slavery in Government Supply Chains – GOV.UK (www.gov.uk)
            </li>
            <li>Following our values-based, safer recruitment procedure</li>
            <li>Complying with child labour laws, including those related to wages, hours worked, overtime, and working conditions.</li>
          </ul>
        </div>

        <div className="policy-block">
          <h2><span className="section-icon"><FaCalendarAlt /></span>Next steps (12 months)</h2>

          <div className="twelve-months-card">
            <div className="twelve-steps">
              <div className="step">
                <div className="step-icon"><FaCheckCircle /></div>
                <div className="step-body">
                  <strong>Embed zero-tolerance</strong>
                  <p>Continue to take action to embed a zero-tolerance approach towards modern slavery, human trafficking, and forced child labour across the organisation and supply chain.</p>
                </div>
              </div>

              <div className="step">
                <div className="step-icon"><FaCheckCircle /></div>
                <div className="step-body">
                  <strong>Staff awareness</strong>
                  <p>Ensure staff know the initial steps to take if they suspect slavery or forced child labour, including reporting channels and emergency actions.</p>
                </div>
              </div>

              <div className="step">
                <div className="step-icon"><FaCheckCircle /></div>
                <div className="step-body">
                  <strong>Annual audits</strong>
                  <p>Audit compliance and supplier practices annually to identify and remediate potential risks or breaches.</p>
                </div>
              </div>

              <div className="step">
                <div className="step-icon"><FaCheckCircle /></div>
                <div className="step-body">
                  <strong>Due diligence</strong>
                  <p>Undertake enhanced due diligence for new suppliers and regularly review existing supplier relationships.</p>
                </div>
              </div>
            </div>
          </div>

        </div>

        <div className="policy-block policy-signature-block">
          <p>
            This Modern Slavery and Human Trafficking Statement will be
            regularly reviewed and updated as necessary. The Managing Director
            endorses this policy statement and is fully committed to its
            implementation.
          </p>

          <div className="signature">
            <div className="signature-left">
              <div className="signature-label">Signed</div>
              <div className="signature-mark"><em>A.Cetin</em></div>
            </div>

            <div className="signature-right">
              <div className="signature-line"><span className="sig-label">Name:</span> <strong>Alper Cetin</strong></div>
              <div className="signature-line"><span className="sig-label">Position:</span> Managing Director</div>
              <div className="signature-line"><span className="sig-label">Date:</span> 18th November 2025</div>
            </div>
          </div>
        </div>

        <div className="policy-block">
          <h2><span className="section-icon"><FaInfoCircle /></span>Employee Information</h2>
          <p>Useful external resources and reporting information:</p>
          <ul>
            <li>
              <a href="https://www.legislation.gov.uk/ukpga/2015/30/section/54" target="_blank" rel="noopener noreferrer">
                Modern Slavery Act 2015 (legislation.gov.uk)
              </a>
            </li>
            <li>
              <span className="nolink" aria-disabled="true">Protecting children from trafficking and modern slavery | NSPCC Learning</span>
            </li>
            <li>
              <span className="nolink" aria-disabled="true">Child Labour Prevention & Remediation – The Centre for Child Rights and Business</span>
            </li>
          </ul>
        </div>

        <div className="policy-block reporting-card">
          <h2><span className="section-icon"><FaExclamationTriangle /></span>Reporting & Emergency</h2>
          <ul>
            <li>Reporting suspected modern slavery or forced child labour as a member of the public</li>
            <li>If an emergency situation, call the police (<strong>999</strong>)</li>
            <li>
              Online: <a href="https://www.modernslaveryhelpline.org/report" target="_blank" rel="noopener noreferrer">File a report</a> (modernslaveryhelpline.org) — Helpline: <strong>0800 0121 700</strong>
            </li>
          </ul>
        </div>
      </section>
    </div>
  );
}

export default ModernSlavery;
